/**
 * Plan 10, Task 3.1 — Camera rig factory.
 * Plan 10, Task 3.2 — Zoom-out + cinematic modes.
 *
 * `createCameraRig(config?)` constructs and returns a
 * `CameraRig & BoardFollowTargetSetter & CinematicMode & ZoomOutMode` object.
 *
 * Mode coverage:
 *   - 'board-follow' — fully implemented (Task 3.1).
 *   - 'zoom-out' — camera moves to target + zoomOutOffset (default 0,30,40).
 *     `setZoomOutOffset` overrides. Factor 1.0 = instant follow (Plan 10).
 *     Plan 18 may add eased ramps.
 *   - 'cinematic' — track-based; linear interpolation between adjacent
 *     keyframes; clamps at last keyframe. Cinematic without a track set is
 *     a no-op (does NOT throw).
 *
 * Design decisions:
 *   - smoothing factor = 1.0 (instant follow) per spec line 641.
 *     Plan 13+ may reduce this for interpolated tracking.
 *   - setBoardFollowTarget clones the incoming Vector3 so the caller
 *     can mutate their copy without affecting the rig's stored target.
 *   - The CameraMode switch in update() uses a `never` exhaustiveness
 *     check so tsc will error when a new mode is added but update()
 *     is not extended.
 *   - cinematicElapsed is reset by resetCinematicClock() and advanced
 *     by update(dt) while mode === 'cinematic'.
 */

import * as THREE from 'three';
import type { CameraRig, CameraMode, CameraRigConfig } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extension interface exposed by the rig returned by `createCameraRig`.
 * Allows callers to drive the board-follow target without accessing
 * internal state directly.
 *
 * @example
 * const rig = createCameraRig();
 * // rig is typed as CameraRig & BoardFollowTargetSetter & CinematicMode & ZoomOutMode
 * rig.setBoardFollowTarget(playerWorldPos);
 */
export interface BoardFollowTargetSetter {
  setBoardFollowTarget(target: THREE.Vector3): void;
}

/**
 * A single keyframe in a cinematic camera track.
 * `at` is seconds since the cinematic clock was last reset.
 */
export interface CinematicKeyframe {
  readonly at: number; // seconds since cinematic start
  readonly position: THREE.Vector3;
  readonly lookAt: THREE.Vector3;
}

/**
 * Cinematic track control methods added to the rig by Task 3.2.
 * `setCinematicTrack` must be called before the rig enters cinematic mode,
 * or the mode is a no-op (graceful "configured but no track yet").
 * `resetCinematicClock` resets the elapsed timer to 0.
 */
export interface CinematicMode {
  setCinematicTrack(keyframes: ReadonlyArray<CinematicKeyframe>): void;
  resetCinematicClock(): void;
}

/**
 * Zoom-out offset control method added to the rig by Task 3.2.
 * Defaults to (0, 30, 40). Override per-scene for wider/narrower overview shots.
 */
export interface ZoomOutMode {
  setZoomOutOffset(offset: THREE.Vector3): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal defaults
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULTS: Required<CameraRigConfig> = {
  initialMode: 'board-follow',
  fov: 50,
  near: 0.1,
  far: 200,
  aspect: 16 / 9,
  boardFollowTarget: new THREE.Vector3(0, 0, 0),
  boardFollowOffset: new THREE.Vector3(0, 8, 12),
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a camera rig that wraps a `THREE.PerspectiveCamera` and manages
 * per-frame update behavior based on the active `CameraMode`.
 *
 * The returned object is typed as
 * `CameraRig & BoardFollowTargetSetter & CinematicMode & ZoomOutMode` so
 * callers can call all control methods directly without casting.
 *
 * @param config - Optional construction overrides. Any omitted key falls back
 *   to `DEFAULTS`.
 * @returns A `CameraRig & BoardFollowTargetSetter & CinematicMode & ZoomOutMode` handle.
 */
export function createCameraRig(
  config?: CameraRigConfig,
): CameraRig & BoardFollowTargetSetter & CinematicMode & ZoomOutMode {
  const fov = config?.fov ?? DEFAULTS.fov;
  const aspect = config?.aspect ?? DEFAULTS.aspect;
  const near = config?.near ?? DEFAULTS.near;
  const far = config?.far ?? DEFAULTS.far;

  const camera = new THREE.PerspectiveCamera(fov, aspect, near, far);

  // Clone incoming vectors so external mutations don't bleed in.
  let currentMode: CameraMode = config?.initialMode ?? DEFAULTS.initialMode;
  let followTarget: THREE.Vector3 = (
    config?.boardFollowTarget ?? DEFAULTS.boardFollowTarget
  ).clone();
  const followOffset: THREE.Vector3 = (
    config?.boardFollowOffset ?? DEFAULTS.boardFollowOffset
  ).clone();

  // Set initial camera position = target + offset, look at target.
  camera.position.copy(followTarget).add(followOffset);
  camera.lookAt(followTarget);

  let disposed = false;

  // ─── Zoom-out state ───────────────────────────────────────────────────────
  // Default offset: (0, 30, 40) gives a broad overview perspective.
  let zoomOutOffset: THREE.Vector3 = new THREE.Vector3(0, 30, 40);

  // ─── Cinematic state ──────────────────────────────────────────────────────
  let cinematicTrack: ReadonlyArray<CinematicKeyframe> | null = null;
  let cinematicElapsed: number = 0;

  // ─── CameraRig implementation ─────────────────────────────────────────────

  function setMode(mode: CameraMode): void {
    // Exhaustiveness check via switch — tsc will error on unhandled modes.
    switch (mode) {
      case 'board-follow':
        currentMode = mode;
        break;
      case 'zoom-out':
        currentMode = mode;
        break;
      case 'cinematic':
        currentMode = mode;
        break;
      case 'spectator':
        // Plan 20b Task 3.1 — SpectatorManager drives camera externally;
        // the rig simply records the mode transition.
        currentMode = mode;
        break;
      default: {
        const _exhaustive: never = mode;
        throw new Error(`Unknown CameraMode: ${String(_exhaustive)}`);
      }
    }
  }

  function update(deltaSeconds: number): void {
    switch (currentMode) {
      case 'board-follow':
        // Smoothing factor = 1.0 (instant); Plan 13+ may lerp here.
        camera.position.copy(followTarget).add(followOffset);
        camera.lookAt(followTarget);
        break;

      case 'zoom-out': {
        // Instant snap to target + zoomOutOffset. Plan 18 may ease this.
        camera.position.copy(followTarget).add(zoomOutOffset);
        camera.lookAt(followTarget);
        break;
      }

      case 'cinematic': {
        // No track set → graceful no-op (design choice: Plan 10).
        if (cinematicTrack === null || cinematicTrack.length === 0) break;

        cinematicElapsed += deltaSeconds;

        const track = cinematicTrack;
        const lastKf = track[track.length - 1];

        // Clamp at the final keyframe.
        if (cinematicElapsed >= lastKf.at) {
          camera.position.copy(lastKf.position);
          camera.lookAt(lastKf.lookAt);
          break;
        }

        // Find the surrounding keyframe pair.
        let prevKf = track[0];
        let nextKf = track[0];
        for (let i = 1; i < track.length; i++) {
          if (track[i].at >= cinematicElapsed) {
            prevKf = track[i - 1];
            nextKf = track[i];
            break;
          }
        }

        // Linear interpolation between adjacent keyframes.
        const span = nextKf.at - prevKf.at;
        const t = span === 0 ? 1 : (cinematicElapsed - prevKf.at) / span;

        const interpolatedPos = new THREE.Vector3().lerpVectors(
          prevKf.position,
          nextKf.position,
          t,
        );
        const interpolatedLookAt = new THREE.Vector3().lerpVectors(prevKf.lookAt, nextKf.lookAt, t);

        camera.position.copy(interpolatedPos);
        camera.lookAt(interpolatedLookAt);
        break;
      }

      case 'spectator':
        // Plan 20b Task 3.1 — camera position/lookAt are driven externally by SpectatorManager
        // via the public 'camera' accessor; this rig branch is a no-op so the exhaustiveness check passes.
        break;

      default: {
        const _exhaustive: never = currentMode;
        throw new Error(`Unknown CameraMode: ${String(_exhaustive)}`);
      }
    }
  }

  function onResize(width: number, height: number): void {
    // Graceful no-op for zero dimensions (e.g. minimised window) —
    // width/height = 0/0 = NaN which would corrupt the projection matrix.
    if (width === 0 || height === 0) return;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    // CameraRig owns no GPU resources; dispose is present for API symmetry.
    // The camera object itself is NOT disposed here — it may still be
    // referenced by an active scene. The caller that added the camera to
    // the scene is responsible for removing it.
  }

  // ─── BoardFollowTargetSetter implementation ───────────────────────────────

  /**
   * Set the board-follow target to a NEW position.  The incoming vector is
   * cloned so the caller can mutate their copy without affecting the rig.
   *
   * In board-follow mode the next `update()` call will position the camera
   * at `target + boardFollowOffset` and point it at `target`.
   */
  function setBoardFollowTarget(target: THREE.Vector3): void {
    followTarget = target.clone();
  }

  // ─── ZoomOutMode implementation ───────────────────────────────────────────

  /**
   * Override the default zoom-out offset `(0, 30, 40)`.
   * The incoming vector is cloned.
   */
  function setZoomOutOffset(offset: THREE.Vector3): void {
    zoomOutOffset = offset.clone();
  }

  // ─── CinematicMode implementation ─────────────────────────────────────────

  /**
   * Attach a track (array of keyframes) to the cinematic mode.
   * Must be called before entering cinematic mode for any behavior to occur.
   * Keyframes should be sorted ascending by `at`; the rig does NOT sort them.
   */
  function setCinematicTrack(keyframes: ReadonlyArray<CinematicKeyframe>): void {
    cinematicTrack = keyframes;
  }

  /**
   * Reset the cinematic elapsed clock to 0.
   * Call before entering cinematic mode to start playback from the beginning.
   */
  function resetCinematicClock(): void {
    cinematicElapsed = 0;
  }

  // ─── Return the full intersection type ───────────────────────────────────

  return {
    camera,
    get mode(): CameraMode {
      return currentMode;
    },
    setMode,
    update,
    onResize,
    dispose,
    setBoardFollowTarget,
    setZoomOutOffset,
    setCinematicTrack,
    resetCinematicClock,
  };
}
